import { useCallback, type RefObject } from 'react';
import { useToast } from '@immo/ui-kit';
import type { ChipInsertHandle } from '@/components/email-template/email-template-chips';
import { resolvePlaceholderValues, type PlaceholderValueSources } from '@/components/email-template/placeholder-values';
import { hasPersonalPage } from '@/components/email-template/personal-link';
import { renderTemplateForComposer } from '@/components/email-template/render-for-composer';
import { useTranslations } from '@/i18n/use-translations';
import { bodyOrNull } from '@/lib/api/body-or-null';
import { apiClient } from '@/lib/auth/api-client';
import { errorCodeOf } from '@/lib/errors';
import { currencyFormatter } from '@/pages/workspace/deals/format';
import { formatNotaryDayNumeric } from '@/pages/workspace/sales-pipeline/notary-appointment-date';
import { usePlaceholderLabel } from '@/pages/settings/email-template-columns';
import { isValidEmail } from './composer-recipient-field';
import type { ComposerTemplateContext, MailAddress } from './use-composers';

export type UseTemplateInsertionParams = {
  readonly workspaceId: string;
  readonly templateContext: ComposerTemplateContext | undefined;
  readonly connectedEmail: string | null;
  readonly to: ReadonlyArray<MailAddress>;
  readonly subjectRef: RefObject<ChipInsertHandle | null>;
  readonly bodyRef: RefObject<ChipInsertHandle | null>;
};

/** Returns the callback that inserts the picked Vorlage into the e-mail being written. */
export function useTemplateInsertion({
  workspaceId,
  templateContext,
  connectedEmail,
  to,
  subjectRef,
  bodyRef,
}: UseTemplateInsertionParams): (emailTemplateId: string) => Promise<void> {
  const t = useTranslations('pages.workspace.mailboxComposer');
  const tRoot = useTranslations();
  const toast = useToast();
  const labels = usePlaceholderLabel();

  return useCallback(
    async (emailTemplateId: string) => {
      const pathParams = { workspaceId };
      const firstTo = to[0]?.email;
      const prospectId = templateContext?.prospectId ?? null;

      const recipient = async () => {
        if (templateContext !== undefined) {
          return bodyOrNull<PlaceholderValueSources['recipient']>(() =>
            apiClient.contacts.workspaceGet({ pathParams: { workspaceId, id: templateContext.contactId } }),
          );
        }

        if (firstTo === undefined || !isValidEmail(firstTo)) return null;

        const matches = await bodyOrNull<{ readonly data: ReadonlyArray<{ readonly id: string }> }>(() =>
          apiClient.contacts.workspaceList({ pathParams, query: { email: firstTo } }),
        );

        const [only, ...rest] = matches?.data ?? [];

        if (only === undefined || rest.length > 0) return null;

        return bodyOrNull<PlaceholderValueSources['recipient']>(() =>
          apiClient.contacts.workspaceGet({ pathParams: { workspaceId, id: only.id } }),
        );
      };

      const [template, recipientBody, property, prospect, profile, company] = await Promise.all([
        apiClient.emailTemplates.get({ pathParams: { workspaceId, emailTemplateId } }).catch((): null => null),
        recipient(),
        templateContext === undefined
          ? null
          : bodyOrNull<PlaceholderValueSources['property']>(() =>
              apiClient.properties.getWorkspace({ pathParams: { workspaceId, id: templateContext.propertyId } }),
            ),
        prospectId === null
          ? null
          : bodyOrNull<PlaceholderValueSources['prospect']>(() =>
              apiClient.prospects.workspaceGet({ pathParams: { workspaceId, id: prospectId } }),
            ),
        bodyOrNull<PlaceholderValueSources['profile']>(() => apiClient.memberProfiles.getMine({ pathParams })),
        bodyOrNull<PlaceholderValueSources['company']>(() => apiClient.company.get({ pathParams })),
      ]);

      if (template === null || !template.ok) {
        const notFound =
          template !== null &&
          template.status === 404 &&
          errorCodeOf(template.body) === 'identity.email-template.not-found';

        toast.danger(notFound ? tRoot('errors.identity.email-template.not-found') : t('template-insert-failed'));

        return;
      }

      const values = resolvePlaceholderValues(
        { recipient: recipientBody, property, prospect, profile, mailboxEmail: connectedEmail, company },
        {
          euro: (amount) => currencyFormatter.format(amount),
          date: (isoDate) => formatNotaryDayNumeric(isoDate, 'de-DE'),
          t,
        },
      );

      const subjectRoot = subjectRef.current?.root();

      // A subject line cannot carry a link, so there the personal link always stays marked.
      if (subjectRoot !== null && subjectRoot !== undefined && (subjectRoot.textContent ?? '').trim() === '') {
        subjectRoot.replaceChildren();
        subjectRef.current?.insertNodesAtCaret([
          ...renderTemplateForComposer(template.body.subject, values, labels).childNodes,
        ]);
      }

      // The body goes last so the selection and focus end after the inserted Text.
      bodyRef.current?.focus();
      bodyRef.current?.insertNodesAtCaret([
        ...renderTemplateForComposer(template.body.body, values, labels, { personalLink: hasPersonalPage(prospect) })
          .childNodes,
      ]);
    },
    [workspaceId, templateContext, connectedEmail, to, subjectRef, bodyRef, t, tRoot, toast, labels],
  );
}
